import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Assessment, DueAction, MarketEvent, NewsItem, PageOf, Report,
              ResearchCase, TradeIdea } from '../api/client'
import { MARKT_REF, alsTag, ausAufgaben, ausEinschaetzungen, ausEreignissen,
         ausFaellen, ausIdeen, ausNews, ausStretch, neuSeit, ordneEintraege,
         ordneFaellig } from './uebersichtRegeln'
import type { AmpelEinschaetzung, FaelligEintrag, Schwellen,
              UebersichtEintrag } from './uebersichtRegeln'

/** Was die Übersicht zeigt, aus allen Listen zusammengetragen. */
export interface UebersichtDaten {
  positiv: UebersichtEintrag[]
  negativ: UebersichtEintrag[]
  faellig: FaelligEintrag[]
  marktlage: AmpelEinschaetzung | null
  /** Die Zahlen der Fußzeile — null, wo die Liste nicht erreichbar war. */
  ohne: {
    ideenVorgeschlagen: number | null
    faelleStill: number | null
    amber: number | null
  }
  /** Seit wann „neu" gilt. */
  seit: Date
}

/** Wie weit die Spalten zurückreichen — eine Woche, mehr ist Archiv. */
const FENSTER_TAGE = 7
/** Wie weit Fällig vorausschaut. */
const VORSCHAU_TAGE = 7
/** Ab wann ein Fall „still liegt". */
const STILL_TAGE = 14

/**
 * Holt die Listen und wendet die Regeln an.
 *
 * AUS DEN VORHANDENEN LISTEN, nicht aus einem eigenen Endpunkt — wie das
 * Verknüpft-Feld der Meldungen. Jede Liste wird weich geholt: Fehlt das Recht
 * oder antwortet sie nicht, bleibt ihr Teil leer, die anderen stehen
 * trotzdem. Erst wenn KEINE Liste antwortet, ist es ein Fehler.
 */
export function useUebersicht(schwellen: Schwellen): {
  laedt: boolean
  fehler: string | null
  daten: UebersichtDaten | null
} {
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [daten, setDaten] = useState<UebersichtDaten | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    setLaedt(true)

    const jetzt = new Date()
    const heute = alsTag(jetzt)
    const seit = neuSeit(jetzt)
    const fenster = new Date(jetzt.getTime() - FENSTER_TAGE * 86_400_000).toISOString()
    const still = new Date(jetzt.getTime() - STILL_TAGE * 86_400_000).toISOString()
    const bis = alsTag(new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() + VORSCHAU_TAGE))

    let antworten = 0
    const weich = <T,>(p: Promise<T>): Promise<T | null> =>
      p.then((x) => { antworten += 1; return x }).catch(() => null)

    Promise.all([
      weich(api.dailyStretch({}, signal)),
      weich(api.news({ since: fenster, sentiment: 'positive', size: 50, sort: 'published_at', dir: 'desc' }, signal)),
      weich(api.news({ since: fenster, sentiment: 'negative', size: 50, sort: 'published_at', dir: 'desc' }, signal)),
      weich(api.assessments({ since: fenster, rating: ['GREEN', 'RED'], size: 50, sort: 'assessed_at', dir: 'desc' }, signal)),
      weich(api.assessments({ since: fenster, rating: ['AMBER'], size: 1, total: true }, signal)),
      weich(api.assessments({ ref: MARKT_REF, size: 1, sort: 'assessed_at', dir: 'desc' }, signal)),
      weich(api.researchCases({ active_only: true, size: 100, sort: 'last_material_update_at', dir: 'desc' }, signal)),
      weich(api.researchCases({ status: 'RESOLVED_POSITIVE', changed_since: fenster, size: 20 }, signal)),
      weich(api.researchCases({ status: 'RESOLVED_NEGATIVE', changed_since: fenster, size: 20 }, signal)),
      weich(api.researchCases({ active_only: true, updated_before: still, size: 1, total: true }, signal)),
      weich(api.tradeIdeas({ open_only: true, size: 100, sort: 'updated', dir: 'desc' }, signal)),
      weich(api.researchActions({ until: bis, limit: 20 }, signal)),
      weich(api.marketEvents({ von: heute, bis, status: ['EXPECTED', 'CONFIRMED'], size: 50 }, signal)),
    ]).then((ergebnis) => {
      if (signal.aborted) return
      const [stretch, newsPlus, newsMinus, ampeln, amber, markt, faelle,
             gutBeendet, schlechtBeendet, stillFaelle, ideen, aufgaben, ereignisse] = ergebnis as [
        Report | null, PageOf<NewsItem> | null, PageOf<NewsItem> | null,
        PageOf<Assessment> | null, PageOf<Assessment> | null, PageOf<Assessment> | null,
        PageOf<ResearchCase> | null, PageOf<ResearchCase> | null, PageOf<ResearchCase> | null,
        PageOf<ResearchCase> | null, PageOf<TradeIdea> | null, DueAction[] | null,
        PageOf<MarketEvent> | null]

      if (antworten === 0) {
        setFehler('Keine der Listen antwortet — Rechte oder Verbindung prüfen.')
        setDaten(null)
        return
      }
      setFehler(null)

      const eintraege = [
        ...(stretch ? ausStretch(stretch, schwellen, heute) : []),
        ...ausNews([...(newsPlus?.items ?? []), ...(newsMinus?.items ?? [])], seit),
        ...ausEinschaetzungen(ampeln?.items ?? [], seit),
        ...ausFaellen([...(faelle?.items ?? []), ...(gutBeendet?.items ?? []),
                       ...(schlechtBeendet?.items ?? [])], schwellen, seit),
        ...ausIdeen(ideen?.items ?? [], seit),
      ]
      const offen = ideen?.items ?? []
      setDaten({
        positiv: ordneEintraege(eintraege.filter((e) => e.vorzeichen === 'positiv')),
        negativ: ordneEintraege(eintraege.filter((e) => e.vorzeichen === 'negativ')),
        faellig: ordneFaellig([
          ...ausAufgaben(aufgaben ?? [], heute),
          ...ausEreignissen(ereignisse?.items ?? [], heute, seit),
        ]),
        marktlage: markt?.items[0] ?? null,
        ohne: {
          ideenVorgeschlagen: ideen ? offen.filter((i) => i.status === 'PROPOSED').length : null,
          faelleStill: stillFaelle?.total ?? null,
          amber: amber?.total ?? null,
        },
        seit,
      })
    }).finally(() => { if (!signal.aborted) setLaedt(false) })

    return () => controller.abort()
  }, [schwellen])

  return { laedt, fehler, daten }
}
